/**
 * useTicketWorkItems - Todos and attachments for an admin report ticket
 *
 * Extracted from useAdminReportData.js. Owns todo CRUD and attachment
 * upload/download/delete, all keeping local state in sync with the API.
 *
 * @param {string} uuid - The report UUID
 * @param {number} timestamp - The report timestamp (sort key)
 * @param {Object} deps
 * @param {Function} deps.clearEscalationState - Reset escalation UI state after actions that restart the clock
 */

import { useState, useCallback } from 'react';
import { Linking } from 'react-native';

import Util from '../util/util';
import { showSuccess } from '../util/error/hookErrors';
import { t } from '../i18n';

const useTicketWorkItems = (uuid, timestamp, { clearEscalationState }) => {
  const [todos, setTodos] = useState([]);
  const [attachments, setAttachments] = useState([]);

  /** Populate todo/attachment state from a fetched report */
  const initializeWorkItems = useCallback((report) => {
    if (Array.isArray(report.todos)) {
      setTodos(report.todos);
    } else {
      setTodos([]);
    }

    if (Array.isArray(report.attachments)) {
      setAttachments(report.attachments);
    } else {
      setAttachments([]);
    }
  }, []);

  /**
   * Add a todo item
   */
  const addTodo = useCallback(
    async (todoText) => {
      if (!todoText.trim()) return null;

      const requestData = {
        body: {
          uuid: uuid,
          timestamp: timestamp,
          todoText: todoText.trim(),
          createdBy: await Util.getUserEmail(),
        },
      };

      try {
        const data = await Util.post('Resty', '/items/todos', requestData);
        clearEscalationState();
        showSuccess(t('reports:ticket.todoAdded'));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Add todo to local state
        if (data && data.todo) {
          setTodos((prev) => [...prev, data.todo]);
        }

        return data.todo;
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.todoAddFailed') });
        return null;
      }
    },
    [uuid, timestamp, clearEscalationState]
  );

  /**
   * Toggle todo completion status
   */
  const toggleTodo = useCallback(
    async (todoId, completed) => {
      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          completed: completed,
          completedBy: completed ? await Util.getUserEmail() : null,
        },
      };

      try {
        const data = await Util.patch('Resty', `/items/todos/${todoId}`, requestData);
        clearEscalationState();
        showSuccess(completed ? t('reports:ticket.todoCompleted') : t('reports:ticket.todoReopened'));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Update local state
        setTodos((prev) =>
          prev.map((t) =>
            t.id === todoId
              ? {
                  ...t,
                  completed: completed,
                  completedBy: completed ? data.todo?.completedBy : null,
                  completedAt: completed ? data.todo?.completedAt : null,
                }
              : t
          )
        );
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.todoUpdateFailed') });
      }
    },
    [uuid, timestamp, clearEscalationState]
  );

  /**
   * Delete a todo item
   */
  const deleteTodo = useCallback(
    async (todoId) => {
      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
        },
      };

      try {
        await Util.del('Resty', `/items/todos/${todoId}`, requestData);
        clearEscalationState();
        showSuccess(t('reports:ticket.todoDeleted'));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Remove from local state
        setTodos((prev) => prev.filter((t) => t.id !== todoId));
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.todoDeleteFailed') });
      }
    },
    [uuid, timestamp, clearEscalationState]
  );

  /**
   * Upload an attachment
   */
  const uploadAttachment = useCallback(
    async (file) => {
      const requestData = {
        body: {
          uuid: uuid,
          timestamp: timestamp,
          file: file.base64,
          filename: file.name,
          contentType: file.type,
          uploadedBy: await Util.getUserEmail(),
        },
      };

      try {
        const data = await Util.post('Resty', '/items/attachments', requestData);
        clearEscalationState();
        showSuccess(t('reports:ticket.attachmentUploaded'));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Add attachment to local state
        if (data && data.attachment) {
          setAttachments((prev) => [...prev, data.attachment]);
        }

        return data.attachment;
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.attachmentUploadFailed') });
        return null;
      }
    },
    [uuid, timestamp, clearEscalationState]
  );

  /**
   * Download an attachment
   */
  const downloadAttachment = useCallback(
    async (attachmentId) => {
      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
        },
      };

      try {
        const data = await Util.get('Resty', `/items/attachments/${encodeURIComponent(attachmentId)}`, requestData);

        if (data && data.url) {
          await Linking.openURL(data.url);
        }

        return data;
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.attachmentDownloadFailed') });
        return null;
      }
    },
    [uuid, timestamp]
  );

  /**
   * Delete an attachment
   */
  const deleteAttachment = useCallback(
    async (attachmentId) => {
      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
        },
      };

      try {
        await Util.del('Resty', `/items/attachments/${encodeURIComponent(attachmentId)}`, requestData);
        showSuccess(t('reports:ticket.attachmentDeleted'));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Remove attachment from local state
        setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));

        return true;
      } catch (err) {
        Util.handleApiError(err, { userMessage: t('reports:ticket.attachmentDeleteFailed') });
        return false;
      }
    },
    [uuid, timestamp]
  );

  return {
    todos,
    attachments,
    initializeWorkItems,
    addTodo,
    toggleTodo,
    deleteTodo,
    uploadAttachment,
    downloadAttachment,
    deleteAttachment,
  };
};

export default useTicketWorkItems;
